// Glazing optics — vetted visible-light transmittance (VLT) & solar-heat-gain coefficient (SHGC)
// presets for the "Glazed" surface tag. Values are representative whole-unit figures from
// LBNL WINDOW 7 / NFRC 200 ratings and the ASHRAE Handbook of Fundamentals Ch.15. Used to
// estimate (a) interior daylight behind glass (interior DLI = site DLI × VLT, per ASHS greenhouse
// transmission) and (b) passive solar heat gain (Q = area × insolation × SHGC, per ASHRAE Ch.15).
// Zero fake math: every coefficient is a published glazing rating, selectable per surface.

export const GLAZING_PRESETS = [
  { id: "single_clear", label: "Single clear", vlt: 0.89, shgc: 0.84 },
  { id: "double_clear", label: "Double clear", vlt: 0.80, shgc: 0.73 },
  { id: "double_lowe", label: "Double low-e", vlt: 0.70, shgc: 0.40 },
  { id: "triple_lowe", label: "Triple low-e", vlt: 0.60, shgc: 0.27 },
];
export const DEFAULT_GLAZING = "double_clear";

// Material-layer predicates (back-compatible with legacy tags that stored the material in `category`).
export const tagIsGlazed = (t) => t?.material === "glazed" || t?.category === "glazed";
export const tagIsOpaque = (t) => t?.material === "opaque" || t?.category === "opaque";

export function glazingPreset(id) {
  return GLAZING_PRESETS.find((p) => p.id === id) || GLAZING_PRESETS.find((p) => p.id === DEFAULT_GLAZING);
}

// Annual irradiation on a vertical façade relative to global horizontal (GHI), by orientation —
// representative mid-latitude ratios from the NREL Solar Radiation Data Manual for Buildings
// (DOE/NREL TP-463-7904). Equator-facing verticals collect most, poleward least, roofs ≈ horizontal.
export const ORIENTATION_FACTORS = { equator: 0.80, eastwest: 0.55, pole: 0.33, horizontal: 1.0 };
const ORIENT_LABEL = { S: "South", N: "North", E: "East", W: "West", roof: "Roof", base: "Ground" };

// Vertical/horizontal irradiance factor for a tagged surface, hemisphere-aware (equator-facing side
// is South in the N hemisphere, North in the S). Roof/ground/unknown ≈ horizontal (1.0).
export function orientationFactor(bucket, lat = 0) {
  if (bucket === "roof" || bucket === "base" || !bucket) return ORIENTATION_FACTORS.horizontal;
  const equatorBucket = lat >= 0 ? "S" : "N";
  const poleBucket = lat >= 0 ? "N" : "S";
  if (bucket === equatorBucket) return ORIENTATION_FACTORS.equator;
  if (bucket === poleBucket) return ORIENTATION_FACTORS.pole;
  if (bucket === "E" || bucket === "W") return ORIENTATION_FACTORS.eastwest;
  return ORIENTATION_FACTORS.horizontal;
}

// Glazing aggregate across every "glazed" tag. `solarGainCoeff` (m²) folds in per-tag SHGC AND the
// orientation irradiance factor, so passive solar = ghi × 365 × solarGainCoeff. `split` breaks the
// glazed area out by façade orientation for the readout. `lat` drives hemisphere orientation.
export function glazingAggregate(surfaceTags, lat = 0) {
  const isGlazed = (t) => t.material === "glazed" || t.category === "glazed";
  const glz = (surfaceTags || []).filter((t) => isGlazed(t) && (t.area || 0) > 0);
  const area = glz.reduce((s, t) => s + (t.area || 0), 0);
  if (!area) return { area_m2: 0, vlt: 0, shgc: 0, count: 0, solarGainCoeff: 0, split: [] };
  let vltA = 0, shgcA = 0, solarGainCoeff = 0;
  const byBucket = {};
  glz.forEach((t) => {
    const p = glazingPreset(t.glazing?.type);
    const a = t.area || 0;
    const f = orientationFactor(t.autoBucket, lat);
    vltA += p.vlt * a; shgcA += p.shgc * a;
    solarGainCoeff += a * p.shgc * f;
    const key = ["N", "S", "E", "W", "roof", "base"].includes(t.autoBucket) ? t.autoBucket : "roof";
    const b = byBucket[key] || (byBucket[key] = { key, label: ORIENT_LABEL[key] || "Horizontal", area_m2: 0, factor: f, gainCoeff: 0 });
    b.area_m2 += a; b.gainCoeff += a * p.shgc * f;
  });
  const split = Object.values(byBucket).sort((x, y) => y.gainCoeff - x.gainCoeff).map((b) => ({ ...b, area_m2: Math.round(b.area_m2) }));
  return { area_m2: area, vlt: vltA / area, shgc: shgcA / area, count: glz.length, solarGainCoeff, split };
}
